import Bugsnag from "@bugsnag/js";
import { Capacitor } from "@capacitor/core";
import { REDIRECT_URI } from "../globals";
import type { AppleSignUp } from "../models/Interfaces";

const APPLE_CLIENT_ID = "com.foxhallwythe.docket.mobile.apple.signin";
// Android can't use Apple's sign-in directly, so Apple's page answers this address, which opens the
// app (an App Link) at /apple-auth/callback: see appleSignupFromCallback.
const ANDROID_REDIRECT_URI = "https://docket.care/apple-auth/callback";

/**
 * Sign in with Apple. On iOS and the web it resolves to what the API signs in with, or null when
 * the user cancelled. On Android it opens Apple's page and resolves to null; the answer comes back
 * to /apple-auth/callback.
 */
export async function appleSignup(): Promise<AppleSignUp | null> {
  if (Capacitor.getPlatform() === "android") {
    const { Browser } = await import("@capacitor/browser");
    await Browser.open({
      url: `https://appleid.apple.com/auth/authorize?client_id=${APPLE_CLIENT_ID}&redirect_uri=${ANDROID_REDIRECT_URI}&response_type=code%20id_token&response_mode=fragment`,
    });
    return null;
  }

  const { SignInWithApple } = await import("@capacitor-community/apple-sign-in");
  let response;
  try {
    ({ response } = await SignInWithApple.authorize({
      clientId: APPLE_CLIENT_ID,
      redirectURI: REDIRECT_URI,
      scopes: "email name",
    }));
  } catch (err) {
    if (cancelled(err)) {
      return null;
    }
    throw err;
  }
  return {
    code: response.authorizationCode,
    token: response.identityToken,
    email: response.email ?? "noEmail@apple.com",
    first_name: response.givenName ?? "firstName",
    last_name: response.familyName ?? "lastName",
  };
}

/**
 * Apple's answer to Android, held here between the link that opened the app and the login page.
 * It never goes into the WebView's URL: Bugsnag records the page's URL and its navigations.
 */
let heldAnswer: string | undefined;

/**
 * Keep the fragment of a link that opened the app at the callback
 * (`https://docket.care/apple-auth/callback#code=…&id_token=…`): Apple's answer.
 */
export function holdAppleAnswer(link: URL): void {
  if (Capacitor.getPlatform() === "android" && isCallback(link) && link.hash.length > 1) {
    heldAnswer = link.hash.slice(1);
  }
}

/** Whether an answer from Apple is waiting for the login page. */
export function appleAnswerWaiting(): boolean {
  return heldAnswer !== undefined;
}

/**
 * The answer Apple sent Android, once, when the login page is at the callback; null if there's
 * none. Only Android signs in this way. Elsewhere anyone could send a link with their own Apple
 * answer and sign a signed-out browser into their account, which would then get the victim's
 * records.
 */
export function appleSignupFromCallback(url: URL): AppleSignUp | null {
  if (Capacitor.getPlatform() !== "android" || !isCallback(url) || heldAnswer === undefined) {
    return null;
  }
  const fragment = new URLSearchParams(heldAnswer);
  heldAnswer = undefined;
  const code = fragment.get("code");
  const token = fragment.get("id_token");
  if (!code || !token) {
    // Apple's answer isn't what we expect: the API will turn it down.
    Bugsnag.notify(new Error("No code or id_token found in Apple Sign In"));
  }
  // Apple only sends the name and address to iOS and the web, the first time.
  return {
    code: code ?? "",
    token: token ?? "",
    email: "noEmail@apple.com",
    first_name: "firstName",
    last_name: "lastName",
  };
}

function isCallback(url: URL): boolean {
  return url.pathname.replace(/\/+$/, "").toLowerCase().endsWith("/apple-auth/callback");
}

/** Whether the user closed Apple's sign-in: the web plugin and the native one say so differently. */
function cancelled(err: unknown): boolean {
  if (typeof err !== "object" || err === null) {
    return false;
  }
  if ("error" in err && err.error === "popup_closed_by_user") {
    return true;
  }
  // ASAuthorizationErrorCanceled (1001), from Apple's ASAuthorizationError.h.
  return (
    "errorMessage" in err &&
    typeof err.errorMessage === "string" &&
    err.errorMessage.includes("com.apple.AuthenticationServices.AuthorizationError error 1001")
  );
}
